'use client';
import { useCallback, useRef } from 'react';
import { isSettingsRevisionConflict } from '../../lib/mutations/personalSettings';
import { SaveConflictError } from '../../lib/saveFeedback';

/** The stale-record barrier for personal settings. Share one instance across controllers that write the same
 *  record (CliSection's settings writers and its permissions writers); unrelated records never share a hold.
 *
 *  A personal-settings write refused as stale (`isSettingsRevisionConflict`) has already had the daemon's current
 *  snapshot adopted into the cache by its mutation, so the next write would silently ride that new revision. This
 *  keeps the writer from doing so: from the first refusal every attempt (a further edit, a debounce, a flush) fails
 *  again as the same conflict WITHOUT a request, until the reader chooses.
 *
 *  - `reload` re-reads, shows what is stored and releases the hold. Every writer has one.
 *  - `retry` (the controller's, for writers that send only the fields the reader changed) is offered as "Keep my
 *    changes": it releases the hold and writes those fields again on the adopted revision, leaving every other
 *    field as stored. Omit it for a writer that replaces a whole block (a map whose order is the payload, a full
 *    settings record): resending that would overwrite what the other session changed.
 *
 *  Returns `write(operation)`, the wrapper every request of that writer goes through. */
export function useRevisionHold(options: { reload: () => Promise<void>; retry?: () => Promise<void> }) {
  const held = useRef(false);
  const latest = useRef(options);
  latest.current = options;
  const conflict = useCallback(() => new SaveConflictError({
    reload: async () => {
      await latest.current.reload();
      held.current = false;
    },
    ...(latest.current.retry ? {
      merge: async () => {
        held.current = false;
        await latest.current.retry?.();
      },
    } : {}),
  }), []);
  return useCallback(async <T>(operation: () => Promise<T>): Promise<T> => {
    if (held.current) throw conflict();
    try {
      return await operation();
    } catch (error) {
      if (!isSettingsRevisionConflict(error)) throw error;
      held.current = true;
      throw conflict();
    }
  }, [conflict]);
}
